import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { HiMiniHome } from "react-icons/hi2";
import { IoIosKey } from "react-icons/io";
import ButtonLoader from "../../components/common/ButtonLoader";
import bgImage from "../../Assets/Images/loginBg.webp"; // full-screen background
import illustration from "../../Assets/Images/loginImg.webp";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const validateForm = () => {
    let valid = true;
    const newErrors = { email: "", password: "" };

    if (!email) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Enter a valid email address.";
      valid = false;
    }

    if (!password) {
      newErrors.password = "Password is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setApiError("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admins/admin-login`,
        { email, password },
        { headers: { "Content-Type": "application/json" } }
      );

      localStorage.setItem("strikes-shots-token", response.data.token);
      localStorage.setItem(
        "strikes-shots-admin",
        JSON.stringify(response.data.admin)
      );

      navigate("/dashboard");
    } catch (error) {
      setApiError(
        error.response?.data?.message || "Something went wrong. Try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative bg-cover bg-center"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60"></div>

      <div className="relative z-10 flex flex-col lg:flex-row w-full max-w-6xl rounded-3xl overflow-hidden shadow-2xl bg-white/90 backdrop-blur-md">
        {/* Left Side Illustration */}
        <div className="hidden lg:block lg:w-1/2 bg-cover bg-center" style={{ backgroundImage: `url(${illustration})` }}></div>

        {/* Login Form */}
        <div className="w-full lg:w-1/2 p-10 relative">
          {/* Decorative Circles */}
          <div className="absolute -top-16 -right-16 w-48 h-48 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor rounded-full opacity-20 animate-pulse"></div>

          <h1 className="text-4xl font-bold text-primaryBtnColor mb-2 text-center lg:text-left">
            Strikes & Shots
          </h1>
          <p className="text-center lg:text-left text-gray-700 mb-8">
            Enter your credentials to continue
          </p>

          {apiError && (
            <p className="text-center text-red-500 mb-4 font-medium">{apiError}</p>
          )}

          <div className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 ${
                  errors.email
                    ? "border-red-500 focus:ring-red-400"
                    : "border-gray-300 focus:ring-primaryBtnColor"
                }`}
                placeholder="admin@gmail.com"
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 ${
                  errors.password
                    ? "border-red-500 focus:ring-red-400"
                    : "border-gray-300 focus:ring-primaryBtnColor"
                }`}
                placeholder="Enter your password"
              />
              {errors.password && (
                <p className="text-xs text-red-500 mt-1">{errors.password}</p>
              )}
            </div>

            {/* Login Button */}
            <div>
              <button
                onClick={handleLogin}
                disabled={isLoading}
                className="w-full h-12 flex justify-center items-center bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition duration-300"
              >
                {isLoading ? <ButtonLoader /> : "Login"}
              </button>
            </div>

            {/* Navigation Links */}
            <div className="flex justify-between text-sm mt-4 text-gray-700">
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-1 hover:text-primaryBtnColor transition"
              >
                <HiMiniHome size={18} /> Home
              </button>
              <button
                onClick={() => navigate("/forgot-password")}
                className="flex items-center gap-1 hover:text-primaryBtnColor transition"
              >
                <IoIosKey size={18} /> Forgot Password
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
